import { useEffect, useState } from 'react';
import { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';

type Params = {
  files: UploadedFile[];
  allOtherFiles: UploadedFile[];
  currentFolder: string | null;
  routeKey: string;
};

export function useFiledDocumentsView({ files, allOtherFiles, currentFolder, routeKey }: Params) {
  const [searchValue, setSearchValue] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    // Any route change (folder click, sidebar link, back/forward) must land on a non-searching view
    setSearchValue('');
    setSearchOpen(false);
  }, [routeKey]);

  const currentFolderFiles = allOtherFiles.filter(
    file => (file.parentId || null) === (currentFolder || null),
  );
  const searchTerm = searchValue.trim().toLowerCase();
  const filteredFiles = searchTerm
    ? allOtherFiles.filter(file => file.file.fileName.toLowerCase().includes(searchTerm))
    : currentFolderFiles;

  const folders = filteredFiles.filter(f => f.isFolder);
  const documents = filteredFiles
    .filter(f => !f.isFolder)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const records = [...folders, ...documents];

  const containingFolderName = (file: UploadedFile) => {
    if (!searchTerm || file.isFolder || !file.parentId) return undefined;
    return files.find(f => f.id === file.parentId && f.isFolder)?.file.fileName;
  };

  const currentFolderRecord = currentFolder
    ? files.find(f => f.id === currentFolder && f.isFolder)
    : undefined;
  const currentFolderName = currentFolder
    ? currentFolderRecord?.file.fileName || 'Unknown folder'
    : 'Filed documents';
  const parentFolderId = currentFolderRecord?.parentId;

  return {
    searchValue,
    setSearchValue,
    searchOpen,
    setSearchOpen,
    filteredFiles,
    records,
    containingFolderName,
    currentFolderName,
    parentFolderId,
  };
}
